import React, { useState, useEffect } from 'react';
import SubscriptionService from '../../services/SubscriptionService';
import './TwitterAccountSettings.css';

const TwitterAccountSettings = ({ user }) => {
  const [accounts, setAccounts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [user.uid]);

  const loadData = async () => {
    try {
      setLoading(true);
      const subscriptionData = await SubscriptionService.checkSubscriptionStatus(user.uid);
      setSubscription(subscriptionData);
      setAccounts(subscriptionData.twitterUsernames || []);
    } catch (error) {
      console.error('Error loading Twitter accounts:', error);
      setError('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    
    const cleanUsername = username.replace('@', '');
    
    if (!/^[a-zA-Z0-9_]{1,15}$/.test(cleanUsername)) {
      return 'Invalid username format';
    }
    
    if (accounts.includes(cleanUsername)) {
      return 'Account already added';
    }
    
    return '';
  };

  const handleAddAccount = async () => {
    const validationError = validateUsername(newUsername);
    if (validationError) {
      setError(validationError);
      return;
    }

    setAdding(true);
    setError('');

    try {
      const cleanUsername = newUsername.replace('@', '');
      await SubscriptionService.addTwitterAccounts(user.uid, [cleanUsername]);
      
      // Reload data to get updated list
      await loadData();
      setNewUsername('');
      setAdding(false);
    } catch (error) {
      console.error('Error adding Twitter account:', error);
      setError(error.message || 'Failed to add account');
      setAdding(false);
    }
  };

  const handleRemoveAccount = async (username) => {
    if (!window.confirm(`Remove @${username} from your accounts?`)) {
      return;
    }

    try {
      await SubscriptionService.removeTwitterAccount(user.uid, username);
      await loadData();
    } catch (error) {
      console.error('Error removing Twitter account:', error);
      setError('Failed to remove account');
    }
  };

  const canAddMore = () => {
    if (!subscription) return false;
    if (subscription.isUnlimited) return true;
    return accounts.length < subscription.maxAccounts;
  };

  if (loading) {
    return (
      <div className="twitter-settings-loading">
        <div className="spinner"></div>
        <p>Loading your Twitter accounts...</p>
      </div>
    );
  }

  return (
    <div className="twitter-account-settings">
      <div className="settings-header">
        <h3>Twitter Accounts</h3>
        <div className="account-limit">
          {subscription?.isUnlimited ? (
            <span className="unlimited">∞ Unlimited</span>
          ) : (
            <span className="limited">
              {accounts.length} / {subscription?.maxAccounts || 0}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      <div className="accounts-list">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">👤</span>
            <p>No Twitter accounts added yet</p>
            <small>Add accounts to start generating content</small>
          </div>
        ) : (
          accounts.map((username, index) => (
            <div key={index} className="account-item">
              <div className="account-info">
                <span className="username">@{username}</span>
                <a 
                  href={`https://twitter.com/${username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-link"
                >
                  View Profile →
                </a>
              </div>
              <button 
                className="remove-btn"
                onClick={() => handleRemoveAccount(username)}
                title="Remove account"
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>

      {canAddMore() && (
        <div className="add-account-section">
          <div className="add-form">
            <div className="username-input-group">
              <span className="username-prefix">@</span>
              <input
                type="text"
                placeholder="username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="username-input"
                onKeyPress={(e) => e.key === 'Enter' && handleAddAccount()}
              />
            </div>
            <button 
              className="add-btn"
              onClick={handleAddAccount}
              disabled={adding || !newUsername.trim()}
            >
              {adding ? '...' : '+ Add'}
            </button>
          </div>
          <small className="add-hint">
            Enter Twitter username without @ symbol
          </small>
        </div>
      )}

      {!canAddMore() && !subscription?.isUnlimited && (
        <div className="limit-reached">
          <span className="limit-icon">🔒</span>
          <div className="limit-content">
            <p>Account limit reached</p>
            <small>Upgrade your plan to add more accounts</small>
          </div>
        </div>
      )}

      <div className="subscription-info">
        <div className="info-header">
          <span className="info-icon">ℹ️</span>
          <span>About Twitter Accounts</span>
        </div>
        <ul className="info-list">
          <li>Accounts are used for content generation and inspiration</li>
          <li>We only read public tweet data</li>
          <li>Your credentials are never stored or accessed</li>
          <li>You can remove accounts anytime</li>
        </ul>
      </div>
    </div>
  );
};

export default TwitterAccountSettings; 